import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import APIConstant from '../../utils/pathConstants';
import api from '../../utils/apiClient';
import Toast from '../Shared/Toast/Toast';
import Loader from '../Shared/Loader/Loader';
import AcademicYearSelector from '../AcademicYearSelector/AcademicYearSelector';
import UpdateBanner from '../UpdateBanner/UpdateBanner';
import useSocket from '../../utils/useSocket';
import useTokenExpiration from '../../utils/useTokenExpiration';
import { useCatchResponse } from '../../utils/useCatchResponse';

const Header = ({ showAcademicSelector = true, isExpanded }) => {
    const { catchResponse } = useCatchResponse();
    const navigate = useNavigate();
    const op = useRef(null);

    const [userData, setUserData] = useState({});
    const [profilePath, setProfilePath] = useState(undefined);
    const [newBuildFound, setNewBuildFound] = useState(false);
    const [isChangePassword, setIsPasswordChange] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [errors, setErrors] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    useSocket(() => {
        if (CommonConfig.getCookie('loggedIn')) {
            setNewBuildFound(true);
        }
    });
    useTokenExpiration();

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        if (loginData?._id) {
            fetchUserDetail(loginData._id);
            getUserAccessList(loginData._id, loginData.Token);

            const handleProfileUpdate = () => fetchUserDetail(loginData._id);
            window.addEventListener("userProfileUpdated", handleProfileUpdate);
            return () => window.removeEventListener("userProfileUpdated", handleProfileUpdate);
        }
    }, []);

    const fetchUserDetail = async (id) => {
        try {
            const res = await api.post(APIConstant.path.getUserById, { id });
            if (res.success) {
                setUserData(res.data);
                setProfilePath(res.data.ProfilePhotoUrl ? `${apiBase}/UserProfilePic/${res.data.ProfilePhotoUrl}` : null);
            }
        } catch (err) {
            catchResponse(err);
        }
    };

    const getUserAccessList = async (userId, token) => {
        try {
            const res = await api.post(APIConstant.path.getUserWiseAccessList, { UserList: [userId] }, {
                header: { 'Authorization': `Bearer ${token}` }
            });
            if (res.success) {
                localStorage.setItem('userAccessData', JSON.stringify(res.data[0]?.ServiceList));
            }
        } catch (err) {
            console.error('Error fetching user access:', err);
        }
    };

    const handleLogout = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.logout, { id: userData?._id });
            if (res.success) {
                Loader.hide();
                CommonConfig.removeCookie('ERPLoginData');
                CommonConfig.removeCookie('access_token');
                CommonConfig.removeCookie('refresh_token');
                CommonConfig.removeCookie('refresh_token');
                CommonConfig.removeCookie('loggedIn')
                CommonConfig.removeCookie('userAccessData');
                CommonConfig.removeCookie('_grecaptcha');
                navigate('/login');
                Toast.success({ message: 'Logged out successfully' });
            } else {
                Loader.hide();
                Toast.error({ message: res.message });
            }
        } catch (err) {
            console.log('errrororor: ', err);

            Loader.hide();
            catchResponse(err);
        }
    };

    const handlePasswordChange = (e, type) => {
        const value = e.target.value;
        setErrors(prev => ({ ...prev, [type]: '' }));
        switch (type) {
            case 'oldPassword':
                setOldPassword(value);
                break;
            case 'newPassword':
                setNewPassword(value);
                break;
            case 'confirmPassword':
                setConfirmPassword(value);
                break;
            default:
                break;
        }
    };

    const validatePassword = () => {
        let isValid = true;
        let newErrors = {};

        if (!newPassword) {
            newErrors.newPassword = 'Please enter new password';
            isValid = false;
        }
        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm password';
            isValid = false;
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Confirm password does not match';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const changePassword = async () => {
        if (!validatePassword()) return;

        try {
            const loginData = CommonConfig.loginData();
            const res = await api.post(APIConstant.path.userChangePassword, {
                UserID: loginData?._id,
                NewPassword: newPassword,
            });
            if (res.success) {
                Toast.success({ message: res.message });
                CommonConfig.getUserMenu(loginData, navigate,'Login');
            } else {
                Toast.error({ message: res.message });
            }
        } catch (err) {
            catchResponse(err);
        }
    };

    const updateBuildConfiguration = async () => {
        try {
            const loginData = CommonConfig.loginData();
            await api.post(APIConstant.path.UpdateBuildConfigurationById, { UserID: loginData?._id });
            setNewBuildFound(false);
            window.location.reload();
        } catch (error) {
            catchResponse(error);
        }
    };

    return (
        <>
            <div className="user" onClick={(e) => op.current.toggle(e)}>
                {newBuildFound && <UpdateBanner onManualRefresh={updateBuildConfiguration} />}
                <div className="user-avatar">
                    <img src={profilePath} alt="" />
                </div>
                {isExpanded ? <div className="user-info">
                    {userData?.Name}
                    <br />
                    <small>{userData?.UserType}</small>
                </div> : null}
            </div>
            <OverlayPanel style={{ left: 20 }} ref={op} className='profile-menu'>
                <ul className='menu-options'>
                    {(showAcademicSelector && userData?.UserType !== 'Parent') && <AcademicYearSelector />}
                    <li className='menu-link' onClick={() => navigate('/edit-profile')}>
                        <i className='icon-edit-profile'></i>
                        <span>Edit Profile</span>
                    </li>
                    <li className='menu-link' onClick={() => setIsPasswordChange(true)}>
                        <i className='icon-change-password'></i>
                        <span>Change Password</span>
                    </li>
                    <li className='menu-link' onClick={handleLogout}>
                        <i className='icon-logout'></i>
                        <span>Logout</span>
                    </li>
                </ul>
            </OverlayPanel>

            <Dialog header="Change Password" visible={isChangePassword} onHide={() => setIsPasswordChange(false)} position="center">
                <div className='p-fluid'>
                    <div className='p-field'>
                        <label htmlFor="oldPassword">Old Password</label>
                        <InputText id="oldPassword" type="password" value={oldPassword} onChange={(e) => handlePasswordChange(e, 'oldPassword')} />
                        {errors.oldPassword && <small className='p-error'>{errors.oldPassword}</small>}
                    </div>
                    <div className='p-field'>
                        <label htmlFor="newPassword">New Password</label>
                        <InputText id="newPassword" type="password" value={newPassword} onChange={(e) => handlePasswordChange(e, 'newPassword')} />
                        {errors.newPassword && <small className='p-error'>{errors.newPassword}</small>}
                    </div>
                    <div className='p-field'>
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <InputText id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => handlePasswordChange(e, 'confirmPassword')} />
                        {errors.confirmPassword && <small className='p-error'>{errors.confirmPassword}</small>}
                    </div>
                </div>
                <div className='p-dialog-footer'>
                    <Button label="Submit" onClick={changePassword} />
                    <Button label="Cancel" className="p-button-secondary" onClick={() => setIsPasswordChange(false)} />
                </div>
            </Dialog>
        </>
    );
};

export default Header;
